/**
 * DashboardHeader — top app bar for authenticated screens.
 *
 * Displays the logo, a notification bell with an unread badge, and a
 * profile dropdown that shows the user's initials, name, and email.
 *
 * Note: MOCK_NOTIFICATIONS is placeholder data — replace with a real
 * data-fetching hook when live notification support is added.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './DashboardHeader.css';
import logo from '../../components/images/logo_white.png';

const MOCK_NOTIFICATIONS = [
  { id: 1, text: 'New evaluation submitted for review', time: '5 min ago', unread: true },
  { id: 2, text: 'Role competency model updated', time: '1 hr ago', unread: true },
  { id: 3, text: 'Development plan has been approved', time: '3 hrs ago', unread: false },
];

function Header({ toggleSidebar, isSidebarOpen }) {
  const { user, profile, signOut, sessionExpiring, extendSession } = useAuth();
  const navigate = useNavigate();
  const [userName, setUserName] = useState('User');
  const [notifOpen, setNotifOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const notifRef = useRef(null);
  const profileRef = useRef(null);

  useEffect(() => {
    if (profile?.first_name || profile?.last_name) {
      const firstName = profile.first_name || '';
      const lastName = profile.last_name || '';
      setUserName(`${firstName} ${lastName}`.trim());
    } else if (user?.user_metadata?.first_name || user?.user_metadata?.last_name) {
      const firstName = user.user_metadata.first_name || '';
      const lastName = user.user_metadata.last_name || '';
      setUserName(`${firstName} ${lastName}`.trim());
    } else {
      const signupData = sessionStorage.getItem('signupData');
      if (signupData) {
        try {
          const data = JSON.parse(signupData);
          if (data.firstName || data.lastName) {
            setUserName(`${data.firstName || ''} ${data.lastName || ''}`.trim());
          }
        } catch (e) {
          // Malformed sessionStorage entry — ignore
        }
      }
    }
  }, [user, profile]);

  useEffect(() => {
    const handler = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /** Returns up to two uppercase initials from the user's display name. */
  const getInitials = () => {
    if (!userName || userName === 'User') return 'U';
    const names = userName.split(' ');
    if (names.length >= 2) return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    return userName.substring(0, 2).toUpperCase();
  };

  const unreadCount = MOCK_NOTIFICATIONS.filter(n => n.unread).length;

  return (
    <>
    {sessionExpiring && (
      <div className="dash-header__session-banner">
        <span>Your session will expire in 5 minutes due to inactivity.</span>
        <button className="dash-header__session-banner-btn" onClick={extendSession}>
          Stay logged in
        </button>
        <button className="dash-header__session-banner-close" onClick={signOut} aria-label="Sign out now">
          Sign out now
        </button>
      </div>
    )}
    <header className="dash-header">
      <div className="dash-header__container">
        <div className="dash-header__left">
          <button
            className={`dash-header__menu-btn ${isSidebarOpen ? 'dash-header__menu-btn--open' : ''}`}
            onClick={toggleSidebar}
            aria-label={isSidebarOpen ? 'Close sidebar' : 'Open sidebar'}
          >
            <svg className="dash-header__arrow-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={isSidebarOpen ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"} />
            </svg>
          </button>

          <div className="dash-header__logo-wrapper">
            <img src={logo} alt="Company Logo" className="dash-header__logo" />
          </div>
        </div>

        <div className="dash-header__actions">

          {/* Notification Bell */}
          <div className="dash-header__notification-wrap" ref={notifRef}>
            <button
              className={`dash-header__notification ${notifOpen ? 'active' : ''}`}
              onClick={() => { setNotifOpen(p => !p); setProfileOpen(false); }}
              aria-label="Notifications"
            >
              <svg className={`dash-header__bell-icon ${unreadCount > 0 ? 'ringing' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              {unreadCount > 0 && (
                <span className="dash-header__notification-badge">{unreadCount}</span>
              )}
            </button>

            {notifOpen && (
              <div className="dash-header__notif-panel">
                <div className="dash-header__notif-header">
                  <span className="dash-header__notif-title">Notifications</span>
                  {unreadCount > 0 && (
                    <span className="dash-header__notif-new-badge">{unreadCount} new</span>
                  )}
                </div>
                <ul className="dash-header__notif-list">
                  {MOCK_NOTIFICATIONS.map(n => (
                    <li key={n.id} className={`dash-header__notif-item ${n.unread ? 'unread' : ''}`}>
                      <div className="dash-header__notif-dot" />
                      <div className="dash-header__notif-body">
                        <p>{n.text}</p>
                        <span>{n.time}</span>
                      </div>
                    </li>
                  ))}
                </ul>
                <button className="dash-header__notif-footer">View all notifications</button>
              </div>
            )}
          </div>

          {/* Profile Dropdown */}
          <div className="dash-header__profile-wrap" ref={profileRef}>
            <button
              className={`dash-header__user-info ${profileOpen ? 'active' : ''}`}
              onClick={() => { setProfileOpen(p => !p); setNotifOpen(false); }}
              aria-label="Account menu"
            >
              <div className="dash-header__avatar">
                <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#080909c9', letterSpacing: '0.5px' }}>
                  {getInitials()}
                </span>
              </div>
              <span className="dash-header__user-name">{userName}</span>
              <svg
                className={`dash-header__profile-chevron ${profileOpen ? 'open' : ''}`}
                viewBox="0 0 24 24" fill="none" stroke="currentColor"
                width="14" height="14"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {profileOpen && (
              <div className="dash-header__profile-dropdown">
                <div className="dash-header__profile-info">
                  <div className="dash-header__profile-avatar-lg">{getInitials()}</div>
                  <div className="dash-header__profile-details">
                    <p className="dash-header__profile-name">{userName}</p>
                    <p className="dash-header__profile-email">{user?.email || ''}</p>
                  </div>
                </div>
                <div className="dash-header__profile-divider" />
                <button
                  className="dash-header__profile-item"
                  onClick={() => { navigate('/profile-settings'); setProfileOpen(false); }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" width="16" height="16">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Profile Settings
                </button>
                <button
                  className="dash-header__profile-item dash-header__profile-item--logout"
                  onClick={signOut}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" width="16" height="16">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Logout
                </button>
              </div>
            )}
          </div>

        </div>
      </div>
    </header>
    </>
  );
}

export default Header;
